import React, { memo } from "react";
import { View, TouchableOpacity, ActivityIndicator } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";

// ─── Action item type ─────────────────────────────────────────────────────────

/**
 * Configuration for a single action button inside ContentActions.
 *
 * `bgColored`/`bgOutlined` are Tailwind class strings applied to the button
 * container depending on the `colored` prop of the parent.
 */
export interface ContentActionItem {
  /** Button label (translation key — the component calls t() internally). */
  label: TranslationKeys;
  onPress: () => void;
  loading?: boolean;
  /** Optional icon rendered to the left of the label. */
  icon?: React.ReactNode;
  // ── Color tokens (Tailwind class strings) ───────────────────────────────
  /** Container class when `colored=true` (solid filled). */
  bgColored: string;
  /** Container class when `colored=false` (outlined/tinted). */
  bgOutlined: string;
  /** Text color class when `colored=true`. */
  textColored: string;
  /** Text color class when `colored=false`. */
  textOutlined: string;
  /** Hex passed to ActivityIndicator when `colored=true`. */
  spinnerColored?: string;
  /** Hex passed to ActivityIndicator when `colored=false`. */
  spinnerOutlined?: string;
  testID?: string;
}

// ─── Pre-built colour sets for the three standard portal actions ─────────────
//
// Spread these into your ContentActionItem definitions at the call site so the
// component stays decoupled from specific business actions:
//
//   actions={[
//     { label: t("review.application.approve"), onPress: handleApprove, ...APPROVE_ACTION_COLORS },
//     { label: t("review.application.reject"),  onPress: handleReject,  ...REJECT_ACTION_COLORS },
//     { label: t("review.application.edit"),    onPress: handleEdit,    ...EDIT_ACTION_COLORS },
//   ]}

export const APPROVE_ACTION_COLORS: Pick<
  ContentActionItem,
  "bgColored" | "bgOutlined" | "textColored" | "textOutlined" | "spinnerColored" | "spinnerOutlined"
> = {
  bgColored: "bg-green-50",
  bgOutlined: "bg-green-5 border border-green-20",
  textColored: "text-white",
  textOutlined: "text-green-50",
  spinnerColored: "#FFFFFF",
  spinnerOutlined: "#009942",
};

export const REJECT_ACTION_COLORS: Pick<
  ContentActionItem,
  "bgColored" | "bgOutlined" | "textColored" | "textOutlined" | "spinnerColored" | "spinnerOutlined"
> = {
  bgColored: "bg-error",
  bgOutlined: "bg-error-light border border-red-20",
  textColored: "text-white",
  textOutlined: "text-error",
  spinnerColored: "#FFFFFF",
  spinnerOutlined: "#EA333C",
};

export const EDIT_ACTION_COLORS: Pick<
  ContentActionItem,
  "bgColored" | "bgOutlined" | "textColored" | "textOutlined" | "spinnerColored" | "spinnerOutlined"
> = {
  bgColored: "bg-blue-40",
  bgOutlined: "bg-blue-5 border border-blue-20",
  textColored: "text-white",
  textOutlined: "text-blue-50",
  spinnerColored: "#FFFFFF",
  spinnerOutlined: "#1F6CFF",
};

// ─── Component ────────────────────────────────────────────────────────────────

export interface ContentActionsProps {
  /**
   * Action buttons to render. Drive this entirely from the outside — the
   * component has no built-in knowledge of Approve / Reject / Edit.
   * Use the exported `*_ACTION_COLORS` spreads for design-system-correct palettes.
   */
  actions: ContentActionItem[];
  /**
   * `true`  → solid filled backgrounds (committed/primary actions).
   * `false` → subtle tinted + outlined style (default, lower emphasis).
   */
  colored?: boolean;
  /** Disables every button in the row. */
  disabled?: boolean;
  testID?: string;
}

/**
 * A configurable row of contextual action buttons.
 *
 * Actions are fully driven by the `actions` prop — the component makes no
 * assumptions about what buttons exist. Export the `*_ACTION_COLORS` constants
 * from this module to compose standard Approve / Reject / Edit items without
 * repeating hex values.
 *
 * Mirrors the "Content Actions" Figma component (node 12190:26460).
 */
export const ContentActions = memo(function ContentActions({
  actions,
  colored = false,
  disabled = false,
  testID,
}: ContentActionsProps) {
  const { t } = useI18n();

  return (
    <View className="flex-row flex-wrap gap-3 items-center" testID={testID}>
      {actions.map((action, index) => {
        const isDisabled = disabled || !!action.loading;
        const containerClass = colored ? action.bgColored : action.bgOutlined;
        const labelClass = colored ? action.textColored : action.textOutlined;
        const spinnerColor = colored
          ? (action.spinnerColored ?? "#FFFFFF")
          : (action.spinnerOutlined ?? "#6C717A");

        return (
          <TouchableOpacity
            key={action.testID ?? `${index}-${action.label}`}
            onPress={action.onPress}
            disabled={isDisabled}
            testID={action.testID}
            accessibilityRole="button"
            accessibilityLabel={t(action.label)}
            accessibilityState={{ disabled: isDisabled, busy: !!action.loading }}
            className={`flex-row items-center justify-center gap-2 px-4 lg:px-6 py-2 lg:py-3 rounded-md ${containerClass} ${
              isDisabled ? "opacity-50" : ""
            }`}
          >
            {action.loading ? (
              <ActivityIndicator size="small" color={spinnerColor} />
            ) : (
              <>
                {!!action.icon && (
                  <View className="items-center justify-center size-5">{action.icon}</View>
                )}
                <AppText weight="medium" className={`text-base tracking-[0.16px] ${labelClass}`}>
                  {t(action.label)}
                </AppText>
              </>
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
});
